'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';

import type { LocationVerificationPillVisual } from '@/logic/geolocation/login-geo-types.types';
import {
    getLoginGeoPillHeartbeatSeq,
    getLoginGeoPillSnapshot,
    subscribeLoginGeoPill
} from '@/logic/geolocation/login-geo-pill-state';
import { getText } from '@/utils/text/text';

import './styles/default.scss';

const ROOT = 'location-verification-pill-bar';
const HEARTBEAT_MS = 450;

const useLocationVerificationPill = (): LocationVerificationPillVisual | null =>
    useSyncExternalStore(subscribeLoginGeoPill, getLoginGeoPillSnapshot, getLoginGeoPillSnapshot);

const useLocationVerificationPillHeartbeatSeq = (): number =>
    useSyncExternalStore(subscribeLoginGeoPill, getLoginGeoPillHeartbeatSeq, getLoginGeoPillHeartbeatSeq);

const PILL_COPY_KEYS: Record<LocationVerificationPillVisual, string> = {
    verifying: 'components.locationVerificationPill.verifying',
    success: 'components.locationVerificationPill.success',
    failed: 'components.locationVerificationPill.failed'
};

const PinIcon = () => (
    <svg className={`${ROOT}__icon`} viewBox="0 0 24 24" aria-hidden>
        <path
            fill="currentColor"
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"
        />
    </svg>
);

const CheckIcon = () => (
    <svg className={`${ROOT}__icon`} viewBox="0 0 24 24" aria-hidden>
        <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
);

const AlertIcon = () => (
    <svg className={`${ROOT}__icon`} viewBox="0 0 24 24" aria-hidden>
        <path fill="currentColor" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
);

const IconForVisual = ({ visual }: { visual: LocationVerificationPillVisual }): React.ReactNode => {
    switch (visual) {
        case 'verifying':
            return <PinIcon />;
        case 'success':
            return <CheckIcon />;
        case 'failed':
            return <AlertIcon />;
        default:
            return null;
    }
};

/**
 * Transient lobby pill while GeoComply runs after login (web / mobile-web only).
 * Copy and palette aligned with Android `LocationVerificationPill` / CAS-56707.
 */
export const LocationVerificationPillBar = () => {
    const visual = useLocationVerificationPill();
    const heartbeatSeq = useLocationVerificationPillHeartbeatSeq();
    const [heartbeatActive, setHeartbeatActive] = useState(false);

    useEffect(() => {
        if (visual !== 'verifying' || heartbeatSeq === 0) {
            return;
        }

        setHeartbeatActive(true);
        const timer = setTimeout(() => setHeartbeatActive(false), HEARTBEAT_MS);
        return () => clearTimeout(timer);
    }, [heartbeatSeq, visual]);

    if (!visual) {
        return null;
    }

    return (
        <div className={ROOT} role="status" aria-live="polite">
            <div
                className={`${ROOT}__chip ${ROOT}__chip--${visual}${heartbeatActive ? ` ${ROOT}__chip--heartbeat` : ''}`}>
                <IconForVisual visual={visual} />
                <span className={`${ROOT}__label`}>{getText({ text: PILL_COPY_KEYS[visual] })}</span>
            </div>
        </div>
    );
};
